import * as React from 'react';
import { PropChangersWrapper } from './PropChangers_SC';
import { returnJSONs, removeQuestionMark } from '../../../utils/fns';
import { DISALLOWED_PROPS, DISALLOWED_PROP_TYPES, errorObjectTextType, errorStatesTextType } from '../../../utils/copy';
import { InputControl } from './PropControls/Input/Input';
import { ToggleControl } from './PropControls/ToggleSwitch/Toggle';
import { PropDownControl } from './PropControls/PropDown/PropDown';
import { returnDropdownMenuForInterface } from './propChangersFns';
import { Contrasts, DropdownObject, _ErrorStatesOptions } from '@inmoment/the-kitchen';
import * as data from '@inmoment/the-kitchen-data';
import { LegacyComponent } from '../../../utils/Documentation';
import { DebugServicePlayground } from '../DebugServicePlayground/DebugServicePlayground';

const errorStateDropdownObject = _ErrorStatesOptions.map<DropdownObject>((item, i) => ({ title: item, id: i.toString() }));

export interface Props<T> {
  componentName: string;
  category: string;
  propObj: T;
  setPropObj: React.Dispatch<React.SetStateAction<T>>;
  contrast?: Contrasts;
  hasContrast?: (hasContrast: boolean) => void;
  componentWrapperElement?: HTMLElement | null;
}

const findComponentPropertiesByName = (name: string, category: string) => {
  let JSONs = returnJSONs();
  return JSONs[category].find((componentProperties) => componentProperties.componentName === name)!;
};

export const PropChangers = <T,>(props: Props<T>) => {
  const { category, componentName, componentWrapperElement } = props;
  const activeComponent = React.useMemo(() => findComponentPropertiesByName(componentName, category), [category, componentName]);

  if (activeComponent?.extends?.includes('BaseText')) {
    activeComponent.color = { type: 'TextColors', default: 'orca', description: '' };
    activeComponent.weight = { type: 'TextWeights', default: '300', description: '' };
    activeComponent.size = { type: 'TextSizes', default: 'body', description: '' };
    activeComponent.level = { type: 'TextLevels', default: '1', description: '' };
  }

  const propChangers = Object.keys(activeComponent)
    .filter((key) => !DISALLOWED_PROPS.includes(key) || (key === 'data' && activeComponent['componentName'] === 'SnackBar'))
    .map((key) => <PropChanger key={key} {...props} activeComponent={activeComponent} propKey={key} />);

  return (
    <PropChangersWrapper id="prop-changers">
      {componentWrapperElement && <DebugServicePlayground componentName={componentName} componentWrapperElement={componentWrapperElement} />}
      {propChangers}
    </PropChangersWrapper>
  );
};

function PropChanger<T>(props: Props<T> & { activeComponent: LegacyComponent; propKey: string }): JSX.Element | null {
  const { activeComponent, contrast, hasContrast, propKey, propObj, componentName, setPropObj } = props;
  const [selected, setSelected] = React.useState<DropdownObject | undefined>();

  const { type } = activeComponent[propKey];
  const formattedKey = removeQuestionMark(propKey) as keyof T & string;
  const formattedKeyString = `${formattedKey}`;
  const buttonType = formattedKey === 'buttons' ? type : '';

  const elementToDisableScroll = [document.getElementById('route-wrapper'), document.getElementById('prop-changers')];

  React.useEffect(() => {
    if (type[0] !== '(' && !DISALLOWED_PROP_TYPES.includes(type) && componentName !== 'ToolTipContent') {
      if (type === 'Contrasts') {
        hasContrast?.(true);
      }
    }
  }, [componentName, hasContrast, type]);

  if (propKey.toLowerCase().includes('hex') || propKey[0] === '_') {
    return null;
  }

  const value = propObj[formattedKey];
  const stringValue = value != null ? `${value}` : undefined;

  if (type[0] !== '(' && !DISALLOWED_PROP_TYPES.includes(type) && componentName !== 'ToolTipContent') {
    switch (type) {
      case "'data-qa'?: string":
        return null;
      case 'string':
      case "string | 'media'":
      case 'number':
      case 'number | string':
      case `number | 'auto'`:
        return (
          <InputControl
            propName={formattedKeyString}
            placeholder={formattedKeyString}
            value={stringValue}
            onChange={(e) => {
              let value: string | number = e.target.value;
              if (type === 'number') {
                value = +value;
              }
              setPropObj((stuff) => ({ ...stuff, [formattedKey]: value }));
            }}
          />
        );
      case 'boolean':
        return (
          <>
            <br />
            <ToggleControl
              onToggle={({ isToggled }) => {
                setPropObj((stuff) => ({ ...stuff, [formattedKey]: isToggled }));
              }}
              propName={formattedKeyString}
              defaultState={propObj[formattedKey] as unknown as boolean}
            />
            <br />
          </>
        );
      case 'MaximumCharacterAllowance':
        return (
          <>
            <InputControl
              propName="maxCharsAllowed.max"
              placeholder="number"
              onChange={(e) => {
                setPropObj((stuff) => ({
                  ...stuff,
                  maxCharsAllowed: { ...(stuff as any).maxCharsAllowed, max: e.target.value },
                }));
              }}
              value={stringValue}
            />
            <InputControl
              propName="maxCharsAllowed.errorMessage"
              placeholder="errorMessage"
              onChange={(e) => {
                setPropObj((stuff) => ({
                  ...stuff,
                  maxCharsAllowed: { ...(stuff as any).maxCharsAllowed, errorMessage: e.target.value },
                }));
              }}
              value={stringValue}
            />
            <ToggleControl
              propName="maxCharsAllowed.allowOvertype"
              onToggle={(state) => {
                setPropObj((stuff) => ({
                  ...stuff,
                  maxCharsAllowed: { ...(stuff as any).maxCharsAllowed, allowOvertype: state },
                }));
              }}
              defaultState={propObj[formattedKey] as unknown as boolean}
            />
          </>
        );
      case buttonType:
        interface ButtonObject {
          text: string;
          key: string;
        }

        const buttonObject = data[`_data${componentName}`].buttons;
        let buttonTexts: ButtonObject[] = [];
        for (let key in buttonObject) {
          buttonTexts.push({ text: `${key}.text`, key });
        }
        return (
          <>
            {buttonTexts.map((item, i) => (
              <InputControl
                key={i}
                propName={item.text}
                placeholder={item.key}
                value={buttonObject[propKey]}
                onChange={(e) => {
                  setPropObj(
                    (stuff) =>
                      ({
                        ...stuff,
                        buttons: {
                          ...(stuff as any).buttons,
                          [item.key]: { text: e.target.value },
                        },
                      } as T),
                  );
                }}
              />
            ))}
          </>
        );
      case errorStatesTextType:
        return (
          <PropDownControl
            value={selected?.id}
            elementToDisableScroll={elementToDisableScroll}
            data={errorStateDropdownObject}
            propName="status"
            interfaceName={type}
            onSelect={(state: DropdownObject) => {
              setSelected(state);
              setPropObj((stuff) => ({ ...stuff, status: state.title }));
            }}
          />
        );
      case errorObjectTextType:
        return (
          <>
            <PropDownControl
              value={selected?.id}
              elementToDisableScroll={elementToDisableScroll}
              data={errorStateDropdownObject}
              propName="status.state"
              interfaceName={type}
              onSelect={(state: DropdownObject) => {
                setSelected(state);
                setPropObj((stuff) => ({
                  ...stuff,
                  status: { ...(stuff as any).status, state: state.title },
                }));
              }}
            />
            <InputControl
              propName={'status.text'}
              placeholder={'text'}
              onChange={(e) => {
                let value = e.target.value;
                setPropObj((stuff) => ({
                  ...stuff,
                  status: { ...(stuff as any).status, text: value },
                }));
              }}
            />
          </>
        );
      case 'Contrasts':
        return (
          <PropDownControl
            value={contrast}
            elementToDisableScroll={elementToDisableScroll}
            data={returnDropdownMenuForInterface(type, selected, true)}
            propName={formattedKeyString}
            interfaceName={type}
            onSelect={(state: DropdownObject) => {
              setSelected(state);
              setPropObj((stuff) => ({ ...stuff, [formattedKey]: state.title }));
            }}
          />
        );
      default:
        let showDropdown = true;
        const dataForPropDown = returnDropdownMenuForInterface(type, selected);
        if (!dataForPropDown.length) {
          showDropdown = false;
        }
        return (
          <>
            {showDropdown ? (
              <PropDownControl
                value={selected?.id}
                elementToDisableScroll={elementToDisableScroll}
                data={dataForPropDown}
                propName={formattedKeyString}
                interfaceName={type}
                onSelect={(state: DropdownObject) => {
                  setSelected(state);
                  setPropObj((stuff) => ({ ...stuff, [formattedKey]: state.title }));
                }}
              />
            ) : null}
          </>
        );
    }
    // custom tooltip props
  } else {
    if (type === 'string') {
      return (
        <>
          <InputControl
            propName="tooltip.text"
            placeholder="Floating Tooltip Text"
            onChange={(e) => {
              setPropObj((stuff) => ({ ...stuff, tooltip: { text: e.target.value } }));
            }}
          />
          <>
            <InputControl
              propName="text"
              value="Text"
              placeholder="Tooltip Main Text"
              onChange={(e) => {
                setPropObj((stuff) => ({ ...stuff, text: e.target.value }));
              }}
            />
            <InputControl
              propName="link"
              placeholder="Tooltip link URL"
              onChange={(e) => {
                setPropObj((stuff) => ({ ...stuff, link: e.target.value }));
              }}
            />
            <em>delete the text & link fields to see the icon show</em>
            <br />
            <br />
            <br />
            <em>iconSize only applies to the icon, which only shows when text and link are undefined</em>
            <br />
            <br />
            <br />
            <PropDownControl
              value={selected?.id}
              elementToDisableScroll={elementToDisableScroll}
              data={returnDropdownMenuForInterface('ButtonSizes', selected)}
              propName="iconSize"
              interfaceName={type}
              onSelect={(state: DropdownObject) => {
                setSelected(state);
                setPropObj((stuff) => ({ ...stuff, iconSize: state.title }));
              }}
            />
          </>
        </>
      );
    }
  }

  return null;
}
